/**
 * TextInput Component
 *
 * Reusable text input with standardized focus styling, error states, and variants.
 * Eliminates 60+ lines of duplicate focus ring and error handling code.
 *
 * @example
 * ```tsx
 * <TextInput
 *   label="Ticker Symbol"
 *   value={ticker}
 *   onChange={(e) => setTicker(e.target.value)}
 *   error={error}
 *   placeholder="e.g., AAPL"
 * />
 * ```
 */

import clsx from 'clsx';
import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label (renders above input) */
  label?: string;
  /** Error message (renders below input with red styling) */
  error?: string | null;
  /** Helper text (renders below input with muted styling) */
  helperText?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Input style variant */
  variant?: 'default' | 'filled';
  /** Left icon element (renders inside input on left) */
  leftIcon?: React.ReactNode;
  /** Additional class name for the container */
  containerClassName?: string;
  /** Additional class name for the label */
  labelClassName?: string;
  /** Additional class name for the input */
  inputClassName?: string;
  /** Additional class name for the input wrapper (used when leftIcon is present) */
  wrapperClassName?: string;
}

const sizeClasses = {
  sm: 'py-1 px-2 text-sm h-8',
  md: 'py-2 px-3 text-base h-9',
  lg: 'py-2.5 px-4 text-base h-10',
};

const variantClasses = {
  default: 'bg-bg',
  filled: 'bg-surface',
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({
  label,
  error,
  helperText,
  size = 'md',
  variant = 'default',
  leftIcon,
  containerClassName,
  labelClassName,
  inputClassName,
  wrapperClassName,
  className,
  id,
  ...inputProps
}, ref) {
  const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  const inputElement = (
    <input
      ref={ref}
      id={inputId}
      className={clsx(
        // Base styles
        'w-full border rounded-sm text-text box-border transition-all duration-fast',
        // Placeholder styles
        'placeholder:text-sm placeholder:font-normal placeholder:text-text-muted',
        // Focus styles - standardized across all inputs
        'focus:outline-none focus:border-primary focus:shadow-[var(--focus-ring-blue-sm)] dark:focus:shadow-[var(--focus-ring-primary)]',
        // Size variant
        sizeClasses[size],
        // Background variant
        variantClasses[variant],
        // Error state
        error ? 'border-danger' : 'border-border',
        // Left padding when icon is present
        leftIcon && 'pl-8',
        // Custom classes
        inputClassName,
        className
      )}
      {...inputProps}
    />
  );

  return (
    <div className={clsx('flex flex-col', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className={clsx(
            'block text-xs font-bold text-text-muted mb-2 uppercase tracking-[0.5px]',
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      {leftIcon ? (
        <div className={clsx('relative', wrapperClassName)}>
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none peer-focus:text-primary">
            {leftIcon}
          </div>
          {inputElement}
        </div>
      ) : (
        inputElement
      )}
      {error && (
        <div className="mt-1 text-xs text-danger bg-red-50 dark:bg-red-900/20 py-1 px-2 rounded-sm border border-danger">
          {error}
        </div>
      )}
      {helperText && !error && (
        <div className="mt-1 text-xs text-text-muted">
          {helperText}
        </div>
      )}
    </div>
  );
});
